/**
 * AudioNodeView — 音频节点内容区域
 *
 * 功能：
 *   ① 真实波形（Web Audio API decodeAudioData → Canvas 2D 绘制）
 *   ② 播放/暂停 + 进度条拖拽
 *   ③ 自适应宽度（根据时长自动调整节点宽度，最大 400px）
 *   ④ ✂️ 快剪按钮 → AudioClipModal 弹窗 → 异步导出新音频节点
 *   ⑤ ☁️ 上传中指示器（裁剪完成后本地可播放，右下角显示 CloudUpload 图标）
 *
 * 宽度策略（仅在音频首次加载时执行一次）：
 *   <5s  → 160px | 5-30s → 200px | 30-120s → 300px | >120s → 400px
 *
 * 波形绘制：
 *   fetch(url) → ArrayBuffer → AudioContext.decodeAudioData → PCM channel 0
 *   → 降采样到 barCount 个 peak → normalize → Canvas fillRect × 2（镜像）
 *   播放进度以颜色区分（已播放 #3b82f6 vs 未播放 rgba(255,255,255,0.15)）
 *
 * 快剪流程（异步非阻塞，与 VideoNodeView 一致）：
 *   ① 弹窗 onExportAsync(in, out) → 立即关闭
 *   ② 创建 loading 音频节点（clipping + Loader2 动画）
 *   ③ FFmpeg.wasm 裁剪（-c copy 零重编码，大文件降级服务端）
 *   ④ 本地 blob 预览（立即可播放）
 *   ⑤ 后台上传 OSS（右下角 ☁️ 提示）
 *   ⑥ 上传完成 → URL 替换为云端
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, Download, RefreshCw, Scissors, Loader2, CloudUpload } from 'lucide-react';
import { downloadFile, buildDownloadFilename, ToolBtn } from './NodeCommons';
import AudioClipModal from '../components/AudioClipModal';
import api from '../api';
import { clipAudio, loadFFmpeg, isFFmpegSupported } from '../services/ffmpegService';
import { getSharedAudioContext } from '../services/audioContextService';

/* ── 工具栏胶囊样式（与 VideoNodeView 一致） ─────────────────────────── */
const capsuleStyle = {
  display: 'flex', gap: '4px', alignItems: 'center',
  background: 'var(--glass-panel)',
  border: '1px solid var(--border-strong)',
  borderRadius: '12px', padding: '4px 6px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
  backdropFilter: 'blur(8px)'
};

const dividerStyle = {
  width: '1px', height: '20px',
  background: 'var(--border-subtle)'
};

/**
 * 根据音频时长计算推荐节点宽度
 * @param {number} dur - 音频时长（秒）
 * @returns {number} 推荐宽度 px
 */
function calcAutoWidth(dur) {
  if (dur < 5)   return 160;
  if (dur < 30)  return 200;
  if (dur < 120) return 300;
  return 400;
}

// P1: React.memo 防止父级 canvas 重渲染时级联到不相关的节点视图
export default React.memo(function AudioNodeView({ id, data, selected, xPos, yPos, zoom = 1 }) {
  // 三态：idle / loading / playing
  const [playState, setPlayState] = useState('idle');
  const isPlaying = playState === 'playing';
  const isBuffering = playState === 'loading';
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showClipModal, setShowClipModal] = useState(false);

  /** 波形 peak 数据：Float32Array，值在 0-1 之间 */
  const [waveform, setWaveform] = useState(null);

  // 优先使用本地缓存 URL（local-cache:// 协议不可直接用作 <audio src>）
  const displayUrl = data.localCacheUrl || (data.output && !data.output.startsWith('local-cache://') ? data.output : null);
  const hasContent = !!displayUrl;

  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  /** 标记是否已执行过自动宽度调整（避免每次 timeUpdate 都触发） */
  const autoWidthDone = useRef(false);
  /** 波形容器引用 + 尺寸状态（ResizeObserver 监听，确保节点缩放后波形重绘） */
  const waveContainerRef = useRef(null);
  const [canvasDims, setCanvasDims] = useState({ w: 0, h: 0 });

  // ── 自适应宽度：音频 metadata 加载后，根据时长调整节点 customSize ──
  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const dur = audio.duration;
    setDuration(dur);
    audio.muted = false;
    audio.volume = 1.0;

    // 仅首次加载时自动调整宽度
    if (!autoWidthDone.current && dur > 0) {
      autoWidthDone.current = true;
      const targetW = calcAutoWidth(dur);
      const currentW = data.customSize?.width || 160;
      if (targetW !== currentW) {
        data.onUpdate?.(id, { customSize: { width: targetW, height: data.customSize?.height || 56 } });
      }
    }
  }, [id, data]);

  // ── 播放控制 ──────────────────────────────────────────────────────
  // 监听 audio 原生事件来驱动三态状态机
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlaying = () => setPlayState('playing');
    const onPause   = () => setPlayState(prev => prev === 'loading' ? prev : 'idle');
    const onEnded   = () => { setPlayState('idle'); setProgress(0); };
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    return () => { audio.removeEventListener('playing', onPlaying); audio.removeEventListener('pause', onPause); audio.removeEventListener('ended', onEnded); };
  }, []);

  /** 播放进度同步：audio timeupdate → 更新 progress（0~1） → Canvas 重绘播放头 */
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const t = audioRef.current.currentTime;
      const d = audioRef.current.duration || 1;
      setProgress(t / d);
    }
  };

  /** 播放/暂停切换（确保 unmute，因为某些浏览器的 autoplay policy 会静音） */
  const togglePlay = (e) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying || isBuffering) {
      audio.pause();
      setPlayState('idle');
    } else {
      audio.muted = false;
      audio.volume = 1.0;
      setPlayState('loading');
      audio.play().catch(() => setPlayState('idle'));
    }
  };

  /** 点击波形区域跳转播放位置（映射点击坐标到时间轴百分比） */
  const handleSeek = (e) => {
    e.stopPropagation();
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = ratio * duration;
    setProgress(ratio);
  };

  // ── Web Audio API 波形提取 ─────────────────────────────────────────
  useEffect(() => {
    if (!displayUrl) return;
    let cancelled = false;

    (async () => {
      try {
        const resp = await fetch(displayUrl);
        if (cancelled) return;
        const buf = await resp.arrayBuffer();
        if (cancelled) return;

        // 🔊 使用全局单例 AudioContext，避免浏览器 6 个上限被耗尽
        const ctx = getSharedAudioContext();
        let audioBuf;
        try { audioBuf = await ctx.decodeAudioData(buf); }
        catch { return; }
        if (cancelled) return;

        const raw = audioBuf.getChannelData(0);
        const BARS = 120; // 降采样到 120 个 peak
        const blockSize = Math.floor(raw.length / BARS) || 1;
        const peaks = new Float32Array(BARS);
        for (let i = 0; i < BARS; i++) {
          let sum = 0;
          const off = i * blockSize;
          for (let j = 0; j < blockSize; j++) sum += Math.abs(raw[off + j] || 0);
          peaks[i] = sum / blockSize;
        }
        const maxP = Math.max(...peaks) || 1;
        for (let i = 0; i < BARS; i++) peaks[i] /= maxP;
        if (!cancelled) setWaveform(peaks);
        // 单例 AudioContext 不关闭，供后续复用
      } catch (e) {
        console.warn('[AudioNodeView] waveform extract failed:', e.message);
      }
    })();

    return () => { cancelled = true; };
  }, [displayUrl]);

  // ── 监听波形容器尺寸变化（节点自适应宽度后自动重绘） ────────────────────
  useEffect(() => {
    const el = waveContainerRef.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      const rw = Math.round(width), rh = Math.round(height);
      setCanvasDims(prev => (prev.w === rw && prev.h === rh) ? prev : { w: rw, h: rh });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── 绘制波形到 Canvas（支持 HiDPI + 自动适配容器尺寸） ────────────────
  // canvasDims 仅作为重绘触发器；实际尺寸在绘制时从容器直接读取，
  // 避免 CSS width:100% 与 canvas.width 位图尺寸的竞态问题。
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = waveContainerRef.current;
    if (!canvas || !container) return;

    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w <= 0 || h <= 0) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);

    if (!waveform) return;

    const barCount = waveform.length;
    const barW = w / barCount;
    const midY = h / 2;
    const playIdx = Math.floor(progress * barCount);

    for (let i = 0; i < barCount; i++) {
      const amp = waveform[i] * midY * 0.85;
      ctx.fillStyle = i <= playIdx ? '#3b82f6' : 'rgba(255,255,255,0.15)';
      ctx.fillRect(i * barW, midY - amp, Math.max(barW - 0.5, 0.5), amp * 2);
    }
  }, [waveform, progress, canvasDims]);

  // 时间格式：MM:SS.cc（厘秒精度）
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00.00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    const cs = Math.floor((seconds % 1) * 100);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}.${cs.toString().padStart(2, '0')}`;
  };

  /* ── 异步快剪导出（非阻塞）──────────────────────────────────────────────
   *
   * 完整流程：
   *   ① 关闭弹窗
   *   ② 创建 loading 音频节点（Loader2 动画）
   *   ③ 后台 FFmpeg.wasm 裁剪（不支持的浏览器直接报错提示）
   *   ④ 裁剪完成 → 本地 blob 预览（立即可播放）
   *   ⑤ 后台异步上传 OSS（右下角 ☁️ 图标提示）
   *   ⑥ 上传完成 → 替换为云端 URL
   *
   * @param {number} clipInPoint  - 裁剪入点（秒）
   * @param {number} clipOutPoint - 裁剪出点（秒）
   */
  /**
   * 音频快剪异步导出处理
   * 
   * 🎯 处理流程：
   * 1. 验证参数（duration ≥ 0.1s）
   * 2. 创建 loading 音频节点（立即关闭弹窗，用户可继续操作）
   * 3. 浏览器端 FFmpeg.wasm 裁剪（MP3 输出，支持 range request）
   * 4. 本地 blob URL 预览（立即可播放）
   * 5. 后台上传 OSS（右下角显示云图标）
   * 
   * 🛡️ 防御措施：
   * - 前置参数验证：clipDuration < 0.1s → alert 提示
   * - FFmpeg 内部验证：duration < 0.1s → throw Error
   * - 详细日志输出：便于追踪 0 秒问题
   */
  const handleAsyncClipExport = useCallback(async (clipInPoint, clipOutPoint) => {
    setShowClipModal(false);                                      // ① 关闭弹窗

    // 🛡️ 前置验证：确保裁剪参数有效（防止用户拖拽入出点过近）
    const clipDuration = clipOutPoint - clipInPoint;
    console.log(`[音频快剪] 收到参数: inPoint=${clipInPoint.toFixed(4)}s, outPoint=${clipOutPoint.toFixed(4)}s, duration=${clipDuration.toFixed(4)}s`);
    
    if (clipDuration < 0.1) {
      alert(`裁剪时长过短（${clipDuration.toFixed(2)}秒），请选择至少 0.1 秒的片段`);
      return;
    }

    // ② 创建 loading 音频节点
    const newNodeId = await data.onDetach?.(id, null, { x: xPos, y: yPos }, 'audio', {
      clipping: true,
    });
    if (!newNodeId) return;

    try {
      // ③ 获取源音频 blob
      const blobResp = await fetch(displayUrl);
      if (!blobResp.ok) throw new Error('无法读取音频数据');
      const audioBlob = await blobResp.blob();

      // 🛡️ 浏览器端裁剪（已移除服务器端降级，不支持的浏览器直接提示）
      if (!isFFmpegSupported()) {
        throw new Error('当前浏览器不支持音频裁剪，请使用 Chrome / Edge 最新版本');
      }

      data.onUpdate?.(newNodeId, { statusInfo: { status: '加载裁剪引擎...' } });
      await loadFFmpeg();
      data.onUpdate?.(newNodeId, { statusInfo: { status: '正在裁剪...' } });
      const clippedBlob = await clipAudio(audioBlob, clipInPoint, clipOutPoint);

      // ④ 裁剪完成 → 本地 blob 预览（立即可播放）
      const localBlobUrl = URL.createObjectURL(clippedBlob);
      data.onUpdate?.(newNodeId, {
        output: localBlobUrl, outputs: [localBlobUrl],
        loading: false, clipping: false,
        uploading: true,
        statusInfo: null, generationStartTime: null, generationModelName: null,
      });

      // ⑤ 后台上传 OSS（用户已可预览，右下角显示 ☁️ 上传图标）
      try {
        const uploadForm = new FormData();
        const ext = clippedBlob.type.includes('mpeg') ? '.mp3' : '.m4a';
        uploadForm.append('file', clippedBlob, `clip-${Date.now()}${ext}`);
        const uploadData = await api.post('/api/upload', uploadForm);
        if (!uploadData.url) throw new Error('服务器未返回音频 URL');
        data.onUpdate?.(newNodeId, {
          output: uploadData.url, outputs: [uploadData.url],
          uploading: false, localCacheUrl: localBlobUrl,
        });
      } catch (uploadErr) {
        console.error('音频快剪上传失败:', uploadErr);
        data.onUpdate?.(newNodeId, { uploading: false, title: '上传失败（本地可预览）' });
      }
    } catch (err) {
      // ⑥ 裁剪失败 → 错误状态
      data.onUpdate?.(newNodeId, {
        loading: false, clipping: false,
        error: err.message || '裁剪失败',
        statusInfo: null, generationStartTime: null, generationModelName: null,
      });
    }
  }, [id, displayUrl, xPos, yPos, data]);

  return (
    <>
      {/* ✂️ 音频快剪弹窗（异步非阻塞）：
        * previewUrl: 当前节点的本地 blob URL（本地优先，避免云存储下载）
        * onExportAsync: 传出入出点后立即关闭弹窗，
        *   handleAsyncClipExport 在后台完成 FFmpeg 裁剪 → 本地播放 → 上传 OSS
        */}
      {showClipModal && (
        <AudioClipModal
          previewUrl={displayUrl}
          onClose={() => setShowClipModal(false)}
          onExportAsync={handleAsyncClipExport}
        />
      )}

      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '3px 5px', boxSizing: 'border-box' }}>
        
        {/* 隐藏的 Audio 标签 */}
        <audio 
          ref={audioRef} 
          src={displayUrl} 
          crossOrigin="anonymous"
          onTimeUpdate={handleTimeUpdate} 
          onLoadedMetadata={handleLoadedMetadata}

        />

        {/* 上半部分：控制栏 + 时间 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
          <div 
            onClick={togglePlay}
            className="nodrag"
            style={{
              width: '16px', height: '16px', 
              borderRadius: '50%',
              background: isBuffering ? '#64748b' : '#3b82f6', 
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: isBuffering ? '0 2px 6px rgba(100,116,139,0.4)' : '0 2px 6px rgba(59, 130, 246, 0.4)',
              transition: 'all 0.15s'
            }}
            onMouseDown={e => e.currentTarget.style.transform = 'scale(0.95)'}
            onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            {isBuffering
              ? <Loader2 size={8} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />
              : isPlaying
                ? <Pause size={7} fill="#fff" color="#fff" />
                : <Play size={7} fill="#fff" color="#fff" style={{ marginLeft: '1px' }} />
            }
          </div>

          <div style={{ 
            flex: 1, minWidth: 0, 
            color: '#fff', fontSize: '8px', fontFamily: 'monospace', 
            fontWeight: '300', letterSpacing: '0.3px', 
            whiteSpace: 'nowrap', overflow: 'hidden'
          }}>
            {formatTime(audioRef.current?.currentTime)} / {formatTime(duration)}
          </div>
        </div>

        {/* 下半部分：真实波形 Canvas + 播放头 */}
        <div 
          ref={waveContainerRef}
          className="nodrag"
          onClick={handleSeek}
          style={{ 
            position: 'relative', width: '100%', flex: 1, minHeight: '18px',
            cursor: 'pointer', borderRadius: '2px', overflow: 'hidden',
          }}
        >
          <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0 }} />
          {/* 波形解码加载指示 */}
          {hasContent && !waveform && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 3,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
            }}>
              <span style={{
                fontSize: 8, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.3px',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}>加载中...</span>
            </div>
          )}
          {/* 播放头 */}
          <div style={{
            position: 'absolute', left: `${progress * 100}%`, bottom: 0, height: '100%', 
            width: '2px', background: 'rgba(255,255,255,0.6)', pointerEvents: 'none',
            transition: 'left 0.05s linear',
          }} />

          {/* ☁️ 上传中指示器：裁剪完成本地可播放，右下角小图标提示后台上传 */}
          {data.uploading && (
            <div style={{
              position: 'absolute', bottom: 1, right: 2, zIndex: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.55)', borderRadius: '50%',
              width: 16, height: 16,
              color: '#93c5fd', pointerEvents: 'none',
              backdropFilter: 'blur(4px)',
            }}>
              <CloudUpload size={9} style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
            </div>
          )}
        </div>
      </div>

      {/* 工具栏 */}
      {selected && (
        <div className="nodrag" style={{ 
            position: 'absolute', top: '100%', left: '0', right: '0', height: '0', zIndex: 1001 
        }}>
          <div style={{
            position: 'absolute',
            top: '2px', left: '50%',
            transform: `translateX(-50%) scale(${(1 / zoom) * 0.9})`,
            transformOrigin: 'top center',
            ...capsuleStyle
          }}>
            {/* ✂️ 快剪按钮 */}
            <ToolBtn 
              onClick={() => {
                // 打开快剪前暂停图板上的音频播放
                if (audioRef.current) { audioRef.current.pause(); setPlayState('idle'); }
                setShowClipModal(true);
              }} 
              disabled={!hasContent}
              title="音频快剪"
            >
              <Scissors size={16} strokeWidth={1.5} />
            </ToolBtn>

            <div style={dividerStyle}></div>

            <ToolBtn onClick={() => { 
              // ⚠️ 下载必须用 displayUrl（blob: 优先），避免 output 为 local-cache:// 时无法下载
              if(displayUrl) downloadFile(displayUrl, buildDownloadFilename(data.title, data.output, '.mp3'));
            }} title="下载">
              <Download size={18} strokeWidth={1.5} />
            </ToolBtn>
            
            <div style={dividerStyle}></div>
            
            <ToolBtn onClick={() => data.onReplace?.(id, 'audio')} title="更换素材">
              <RefreshCw size={16} strokeWidth={1.5} />
            </ToolBtn>
          </div>
        </div>
      )}
    </>
  );
});